'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useNotifications } from '@/hooks/useNotifications';
import { useCriticalNotifications } from '@/hooks/useCriticalNotifications';
import { CriticalToast } from './critical-toast';

// Mounts at the portal root and orchestrates the three-tier delivery for
// critical notifications. Tier A is rendered inside the chat view via the
// ChatBannerProvider; Tier B is rendered here (modal toast); Tier C is
// rendered by the OS via the Service Worker push handler.
//
// We only ever render the head-of-queue modal-toast entry to avoid stacking
// multiple critical interruptions. When the user dismisses or actions on it,
// the next entry surfaces.

export function CriticalNotificationOverlay() {
  const router = useRouter();
  // `notifications` is the bell's first page, already fetched here for
  // `performAction`. Handing it to the critical hook is what puts a request
  // that is STILL PENDING back on screen after a reload (2026-09-07): the
  // queue used to be built from live WebSocket events only, so reloading with
  // an unanswered publish approval left the bell as the only surface while
  // the agent told the person to look for a prompt on their screen. No extra
  // request: these rows are already in hand. See `isPendingCritical` for what
  // is eligible and why payment and permission prompts are not.
  const { performAction, notifications } = useNotifications(true);
  const { queue, dismiss } = useCriticalNotifications({ known: notifications });
  const originalTitleRef = useRef<string | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const head = queue.find((e) => e.tier === 'modal_toast');

  // Tab-title flash + favicon dot while there are unacted critical entries.
  // Resets on focus/visibilitychange + when queue empties.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const hasUnacted = queue.length > 0;

    if (!hasUnacted) {
      if (flashTimerRef.current) {
        clearInterval(flashTimerRef.current);
        flashTimerRef.current = null;
      }
      if (originalTitleRef.current !== null) {
        document.title = originalTitleRef.current;
        originalTitleRef.current = null;
      }
      removeFaviconDot();
      return;
    }

    if (originalTitleRef.current === null) {
      originalTitleRef.current = document.title;
    }

    addFaviconDot();

    let flipped = false;
    const tick = () => {
      // Don't flash while focused — the user is in-app, the overlay/banner
      // is doing its job. Flash only when the tab is backgrounded.
      if (document.visibilityState === 'visible' && document.hasFocus()) {
        if (originalTitleRef.current !== null) document.title = originalTitleRef.current;
        return;
      }
      flipped = !flipped;
      const base = originalTitleRef.current ?? document.title;
      document.title = flipped ? `(!) Action required - ${base}` : base;
    };

    flashTimerRef.current = setInterval(tick, 1500);
    tick();

    const onFocus = () => {
      if (originalTitleRef.current !== null) document.title = originalTitleRef.current;
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      if (flashTimerRef.current) {
        clearInterval(flashTimerRef.current);
        flashTimerRef.current = null;
      }
    };
  }, [queue.length]);

  if (!head) return null;

  async function handleAction(
    action:
      | 'approve'
      | 'deny'
      | 'always_approve'
      | 'approve_once'
      | 'reject',
    /** Dollar threshold from the toast's "Set limit" picker. Only
     *  consumed when `action === 'always_approve'`. */
    threshold?: number,
  ) {
    if (!head) return;
    const id = head.notification.id;
    try {
      if (
        head.notification.type === 'tool_approval' ||
        head.notification.type === 'widget_permission_request' ||
        // App publish approvals go the same way (2026-09-06). They must NOT
        // fall through to `performAction`, which is payment-shaped and posts
        // a `threshold`/`duration` the publish branch has no meaning for.
        head.notification.type === 'widget_publish_request'
      ) {
        // notify-policy tool approvals + per-widget permission grants
        // both route through the generic notifications PATCH handler
        // (action: 'approve' | 'reject' | 'deny'). Non-blocking — the
        // grant is persisted server-side and the workspace tab reloads
        // the iframe off the emitted `workspace.permission_granted`.
        await fetch(`/api/notifications/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action }),
        });
      } else {
        await performAction(
          id,
          action as 'approve' | 'deny' | 'always_approve' | 'approve_once',
          undefined,
          threshold,
        );
      }
    } finally {
      dismiss(id);
    }
  }

  function handleDismiss() {
    if (!head) return;
    dismiss(head.notification.id);
  }

  function handleView() {
    if (!head) return;
    const data = head.notification.data as Record<string, unknown> | null;
    const chatId = data?.chatId as string | undefined;
    if (chatId) {
      router.push(`/c/${chatId}`);
    }
    dismiss(head.notification.id);
  }

  return (
    <CriticalToast
      notification={head.notification}
      onAction={handleAction}
      onDismiss={handleDismiss}
      onView={handleView}
    />
  );
}

// ---- favicon dot helpers (best-effort; silently no-op if unavailable) ----

const FAVICON_ID = 'robutler-critical-favicon';

function addFaviconDot() {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(FAVICON_ID) as HTMLLinkElement | null;
  if (existing) return;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Try to copy current favicon as base
    const current = document.querySelector<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]');
    const draw = () => {
      ctx.beginPath();
      ctx.arc(24, 8, 7, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();
      const link = document.createElement('link');
      link.id = FAVICON_ID;
      link.rel = 'icon';
      link.href = canvas.toDataURL('image/png');
      document.head.appendChild(link);
    };
    if (current?.href) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try { ctx.drawImage(img, 0, 0, 32, 32); } catch {}
        draw();
      };
      img.onerror = draw;
      img.src = current.href;
    } else {
      draw();
    }
  } catch { /* no-op */ }
}

function removeFaviconDot() {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(FAVICON_ID);
  existing?.parentNode?.removeChild(existing);
}
